/**
 * Key Contacts Utilities
 *
 * Pure utility functions for filtering and processing key contacts data.
 * Extracted from CompanyDetails.jsx for better testability and maintainability.
 */

import { getContactIndividualId } from '../../../../utils/idUtils';

/**
 * Age range values matching UI_OPTIONS[UI_OPTION_KEYS.CONTACT_FILTER_AGE]
 * These are the string values used in the filter UI
 */
export const AGE_RANGES = {
  UNDER_30: '<30',
  THIRTIES: '30 - 39',
  FORTIES: '40 - 49',
  FIFTIES: '50 - 59',
  SIXTY_PLUS: '60+'
};

/**
 * Is shareholder filter values
 */
export const IS_SHAREHOLDER_VALUES = {
  YES: 'Yes',
  NO: 'No'
};

/**
 * Creates an age checker function for a given age range string
 * @param {string} ageRange - Age range string (e.g., '<30', '30 - 39', '60+')
 * @returns {Function} Checker function that takes an age and returns boolean
 */
export const createAgeChecker = (ageRange) => {
  switch (ageRange) {
    case AGE_RANGES.UNDER_30:
      return (age) => age < 30;
    case AGE_RANGES.THIRTIES:
      return (age) => age >= 30 && age <= 39;
    case AGE_RANGES.FORTIES:
      return (age) => age >= 40 && age <= 49;
    case AGE_RANGES.FIFTIES:
      return (age) => age >= 50 && age <= 59;
    case AGE_RANGES.SIXTY_PLUS:
      return (age) => age >= 60;
    default:
      return () => false;
  }
};

/**
 * Returns the URL unchanged when it is a syntactically valid http(s) URL, else
 * null. Server-supplied URLs (e.g. a discovered contact's `linkedin_url`) must be
 * scheme-checked before `window.open`, since a `javascript:`/`data:` URL would
 * execute in the opener context. Callers fall back to a safe constructed URL.
 * @param {string} url - Candidate URL (may be undefined/empty/untrusted)
 * @returns {string|null} The URL if http/https, otherwise null
 */
export const safeHttpUrl = (url) => {
  if (typeof url !== 'string' || !url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};

/**
 * Case-insensitive country normaliser for filter matching. Discovered
 * contacts carry Surfe's country strings (full English names, TD-2358)
 * while the filter options derive from stored CREFO contacts — normalise
 * both sides so casing differences alone don't exclude a contact (TD-2273).
 * @param {*} value - Candidate country value (may be undefined/null)
 * @returns {string} Trimmed lowercase string ('' when absent)
 */
const normalizeCountry = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

/**
 * Stable selection key spanning stored + discovered contacts (TD-2421). Stored
 * CREFO contacts key on their platform `id`; ephemeral discovered contacts have
 * no `id`, only a synthesised `disc_<uuid>` `person_crefo_id`. One key lets a
 * single selection Set and "Select all" span both lists.
 * @param {Object} contact
 * @returns {string|number|undefined}
 */
export const contactKey = (contact) => contact?.id ?? contact?.person_crefo_id;

/**
 * TD-2421: a contact is "discovered" (ephemeral, Surfe-sourced) when tagged as
 * such or carrying a synthesised `disc_` `person_crefo_id`. Mirrors ContactCard's
 * `rowIsDiscovered` without the render-context `source` argument.
 * @param {Object} contact
 * @returns {boolean}
 */
export const isDiscoveredContact = (contact) =>
  contact?.source === 'discovered' || String(contact?.person_crefo_id ?? '').startsWith('disc_');

/**
 * Individual-profile path of a key contact, with the company as the back-button label,
 * or null when the contact has no profile. Shared by same-tab and new-tab opening, the
 * counterpart of shareholderGraphUtils' getNodeDetailPath for graph nodes.
 * @param {Object} contact
 * @param {string} companyName - Current company name (back button "Back to …")
 * @param {{ getIndividualDetailsPath: Function }} utilsStore
 * @returns {string|null}
 */
export const getKeyContactDetailPath = (contact, companyName, utilsStore) => {
  const individualId = getContactIndividualId(contact);
  return individualId
    ? utilsStore.getIndividualDetailsPath(individualId, { ref: companyName })
    : null;
};

/**
 * TD-2421/TD-2357: split already skip-eligible contacts into the two bulk-reveal
 * identity channels — stored `contactIds` (a `contact_enrichment_id`) and
 * ephemeral `discoveredContacts` identities (first/last/linkedin, plus the Surfe
 * seniority/job_title/country the BE persists at mint — TD-2667). A row is routed
 * to `discoveredContacts` only when it has NO
 * `contact_enrichment_id`, is discovered, and carries the first + last name the
 * mint requires (the BE 400s otherwise).
 * @param {Array} eligibleContacts
 * @returns {{ contactIds: Array, discoveredContacts: Array }}
 */
export const partitionRevealTargets = (eligibleContacts = []) => {
  const contactIds = eligibleContacts.map((c) => c.contact_enrichment_id).filter(Boolean);
  const discoveredContacts = eligibleContacts
    .filter((c) => !c.contact_enrichment_id && isDiscoveredContact(c))
    .map((c) => ({
      first_name: c.first_name,
      last_name: c.last_name,
      linkedin_url: c.linkedin_url ?? null,
      // TD-2667: send the same discovery-time attributes as the single mint path
      // so bulk-minted discovered contacts also keep their Role/residency. Uses
      // the clean `seniority` enum (TD-2292), never `manager_function` — that one
      // is the BE `role` scalar and falls back to the free-text job title when
      // Surfe returns no seniority, which the mint would then lock into
      // `seniorities[]` past the webhook's write-once top-up.
      ...(c.seniority ? { seniority: c.seniority } : {}),
      ...(c.job_title ? { job_title: c.job_title } : {}),
      ...(c.country ? { country: c.country } : {})
    }))
    .filter((c) => c.first_name && c.last_name);
  return { contactIds, discoveredContacts };
};

/**
 * Filter key contacts based on provided filter criteria
 * Applies to stored CREFO contacts and discovered contacts alike; the
 * country filter matches a discovered contact's Surfe `country` field
 * case-insensitively (TD-2273), while age/shareholder filters test
 * CREFO-only fields (discovered contacts never match those).
 * @param {Array} contacts - Array of contact objects
 * @param {Object} filters - Filter criteria object
 * @param {Array} filters.role - Array of role values to filter by
 * @param {Array} filters.country - Array of country values to filter by
 * @param {Array} filters.is_shareholder - Array with 'Yes' and/or 'No' values
 * @param {Array} filters.age - Array of age range strings (e.g., ['<30', '60+'])
 * @returns {Array} Filtered contacts array
 */
export const filterKeyContacts = (contacts, filters) => {
  if (!contacts?.length) return [];

  const {
    role: roleFilters = [],
    country: countryFilters = [],
    is_shareholder: isShareholderFilters = [],
    age: ageFilters = []
  } = filters;

  // Create age checker functions for selected age ranges
  const ageCheckers = ageFilters.length ? ageFilters.map(createAgeChecker) : null;

  return contacts.filter((contact) => {
    // Filter by role (TD-2422: bucket-aware, cross-source). Each contact carries
    // a canonical `role_bucket` label — BE-stamped for CREFO key contacts
    // (TD-2330), FE-stamped for discovered contacts (mapDiscoveredContact).
    // Matching the raw `manager_function` never matched discovered rows, whose
    // value is a Surfe seniority rather than a CREFO management function.
    //
    // TD-2956: match on MEMBERSHIP, not on the single role. A person can belong to two
    // buckets — `company_managers` collapses their roles to one `manager_function` and
    // prefers the non-ownership one, so a co-owning Geschäftsführer arrives as "Managing
    // Director" with only an `is_shareholder` flag. The BE now sends `role_buckets` with
    // both, and without this the majority shareholder is absent from the Shareholder filter
    // while their own card reads "Is also a shareholder: Yes".
    //
    // The fallback is load-bearing, not defensive: contacts added mid-session through
    // `mapDiscoveredContact` never round-trip through the BE, so they carry only
    // `role_bucket`. Dropping them from every role filter would regress TD-2422. It is also
    // exactly right — a discovered contact's membership IS just its role bucket, since Surfe
    // carries no ownership signal (both discovered arms select NULL as is_shareholder).
    const contactBuckets = contact.role_buckets ?? [contact.role_bucket];
    if (roleFilters.length && !contactBuckets.some((b) => roleFilters.includes(b))) {
      return false;
    }

    // Filter by country (case-insensitive; contacts without a country never
    // match an active country filter)
    if (countryFilters.length) {
      const contactCountry = normalizeCountry(contact.country);
      if (
        !contactCountry ||
        !countryFilters.some((filterValue) => normalizeCountry(filterValue) === contactCountry)
      ) {
        return false;
      }
    }

    // Filter by is_shareholder (only apply if exactly one value selected)
    if (isShareholderFilters.length === 1) {
      const shouldBeShareholder = isShareholderFilters[0] === IS_SHAREHOLDER_VALUES.YES;
      if (contact.is_shareholder !== shouldBeShareholder) {
        return false;
      }
    }

    // Filter by age (contact matches if in ANY of the selected age ranges)
    if (ageCheckers && !ageCheckers.some((check) => check(contact.age))) {
      return false;
    }

    return true;
  });
};
